import { Skeleton } from "@/shared/ui/skeleton"

import { cn } from "@/shared/lib/utils"

interface SettingsHubSkeletonProps extends React.ComponentProps<"div"> {
  /** Tile count per section, in page order. */
  sections: readonly number[]
  /** The balance card the AI hub shows above its sections. */
  withSummaryCard?: boolean
}

const TileSkeleton: React.FC = () => (
  <div className="grid min-h-12 grid-cols-[1.75rem_minmax(0,_1fr)] items-center gap-x-2 px-2 py-2">
    <Skeleton className="size-7 rounded-lg" />
    <Skeleton className="h-3.5 w-3/4" />
  </div>
)

/** Mirrors the moderation and AI hubs (header, group pill, grid entry, tools row, tile sections) so nothing jumps on load. */
export const SettingsHubSkeleton: React.FC<SettingsHubSkeletonProps> = ({
  sections,
  withSummaryCard = false,
  className,
  ...props
}) => (
  <div
    {...props}
    aria-busy="true"
    className={cn("space-y-4", className)}>
    <div className="flex items-start justify-between gap-3">
      <div className="space-y-2 pt-1">
        <Skeleton className="h-6 w-44" />
        <Skeleton className="h-6 w-28" />
      </div>
      <div className="flex items-center gap-2">
        <Skeleton className="size-9 rounded-full" />
        <Skeleton className="h-9 w-20 rounded-full" />
      </div>
    </div>
    <Skeleton className="mx-auto h-9 w-60 max-w-full rounded-full" />
    <Skeleton className="h-14 rounded-2xl" />
    <div className="flex items-center gap-3 px-1">
      <Skeleton className="size-6 rounded-md" />
      <div className="flex-1 space-y-1.5">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-3 w-52 max-w-full" />
      </div>
    </div>
    {withSummaryCard ? <Skeleton className="h-28 rounded-2xl" /> : null}
    {sections.map((tileCount, sectionIndex) => (
      <section
        key={sectionIndex}
        className="rounded-2xl bg-gray-800">
        <div className="flex min-h-12 items-center gap-3 px-4 py-2.5">
          <Skeleton className="size-5 rounded-md" />
          <Skeleton className="h-4 w-32" />
        </div>
        <div className="grid grid-cols-2 gap-x-1 gap-y-0.5 px-2 pb-2">
          {Array.from({ length: tileCount }, (_, tileIndex) => (
            <TileSkeleton key={tileIndex} />
          ))}
        </div>
      </section>
    ))}
  </div>
)
